import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../ui/Modal';
import { Button } from '../ui/Button';
import TextInput from '../ui/TextInput';
import { useTheme, type ThemeColors } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { cn } from '../../lib/utils';
import { XIcon, SearchIcon, TrashIcon, StarIcon, GripVerticalIcon, PencilIcon, CheckIcon } from '../ui/Icons';
import {
  getFavorites,
  addToFavorites,
  addMultipleToFavorites,
  removeFromFavorites,
  reorderFavorites,
  getAllMenuItems,
  FavoriteItem,
} from '../../utils/favorites';
import { getSidebarItems } from './config/SidebarItems';
import { useAuth } from '../../contexts/AuthContext';

interface FavoritesManagerProps {
  open: boolean;
  onClose: () => void;
}

type AvailableItem = { title: string; to: string; icon?: React.ReactNode; fullPath: string };

const FavoriteRow = React.memo(function FavoriteRow(props: {
  fav: FavoriteItem;
  index: number;
  isEditing: boolean;
  editingTitle: string;
  onClose: () => void;
  onEditingTitleChange: (value: string) => void;
  onStartEdit: (fav: FavoriteItem) => void;
  onSaveEdit: (route: string) => void;
  onCancelEdit: () => void;
  onRemove: (route: string) => void;
  onDragStart: (e: React.DragEvent, index: number) => void;
  onDragEnd: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent, index: number) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent, index: number) => void;
  draggedIndex: number | null;
  dragOverIndex: number | null;
  colors: ThemeColors;
}) {
  const {
    fav,
    index,
    isEditing,
    editingTitle,
    onClose,
    onEditingTitleChange,
    onStartEdit,
    onSaveEdit,
    onCancelEdit,
    onRemove,
    onDragStart,
    onDragEnd,
    onDragOver,
    onDragLeave,
    onDrop,
    draggedIndex,
    dragOverIndex,
    colors,
  } = props;
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, index)}
      onDragEnd={onDragEnd}
      onDragOver={(e) => onDragOver(e, index)}
      onDragLeave={onDragLeave}
      onDrop={(e) => onDrop(e, index)}
      className={cn(
        'flex items-center justify-between p-3 transition-all cursor-move',
        colors.surface,
        draggedIndex === index && 'opacity-50',
        dragOverIndex === index && cn('border-t-2', colors.primaryBorderAll.replace('border-', 'border-t-'), colors.surfaceHover),
        dragOverIndex !== index && 'hover:bg-gray-50 dark:hover:bg-gray-800'
      )}
    >
      <div className={cn('p-1 mr-2 cursor-grab active:cursor-grabbing', colors.textMuted)}>
        <GripVerticalIcon className="w-4 h-4" />
      </div>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <StarIcon className="w-4 h-4 text-yellow-500 flex-shrink-0" filled />
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="flex items-center gap-2">
              <TextInput
                type="text"
                value={editingTitle}
                onChange={(e: { target: { value: string } }) => onEditingTitleChange(e.target.value)}
                className="h-8 text-sm"
                autoFocus
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    onSaveEdit(fav.to);
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    onCancelEdit();
                  }
                }}
              />
              <button
                onClick={() => onSaveEdit(fav.to)}
                className={cn('p-1 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors', colors.textSecondary)}
                title="Save name"
              >
                <CheckIcon className="w-4 h-4 text-green-600" />
              </button>
              <button
                onClick={onCancelEdit}
                className={cn('p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors', colors.textSecondary)}
                title="Cancel"
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              <Link
                to={fav.to}
                className="flex items-center gap-3 flex-1 min-w-0"
                onClick={onClose}
                draggable={false}
              >
                <div className="flex flex-col min-w-0">
                  <div className={cn('text-sm font-medium truncate', colors.text)}>{fav.title}</div>
                  <div className={cn('text-xs truncate', colors.textSecondary)}>
                    {fav.fullPath || fav.to}
                  </div>
                </div>
              </Link>
            </>
          )}
        </div>
      </div>
      {!isEditing && (
        <button
          onClick={() => onStartEdit(fav)}
          className={cn('p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ml-2', colors.textSecondary)}
          title="Rename"
          draggable={false}
        >
          <PencilIcon className="w-4 h-4" />
        </button>
      )}
      <button
        onClick={() => onRemove(fav.to)}
        className={cn('p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors ml-1', colors.textSecondary)}
        title="Remove from favorites"
        draggable={false}
      >
        <TrashIcon className="w-4 h-4 text-red-500" />
      </button>
    </div>
  );
});

const AvailableItemRow = React.memo(function AvailableItemRow(props: {
  item: AvailableItem;
  isFavorite: boolean;
  isSelected: boolean;
  onToggleSelect: (item: { to: string }) => void;
  onAddFavorite: (item: { title: string; to: string; fullPath?: string }) => void;
  onRemoveFavorite: (route: string) => void;
  colors: ThemeColors;
}) {
  const { item, isFavorite, isSelected, onToggleSelect, onAddFavorite, onRemoveFavorite, colors } = props;
  return (
    <div
      className={cn(
        'flex items-center justify-between p-3 transition-colors',
        isFavorite
          ? 'bg-yellow-50 dark:bg-yellow-900/10 border-l-4 border-yellow-500'
          : isSelected
          ? 'bg-blue-50 dark:bg-blue-900/10'
          : 'hover:bg-gray-50 dark:hover:bg-gray-800',
        colors.surface
      )}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {!isFavorite && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect(item)}
            className={cn(
              'w-4 h-4 rounded focus:ring-2',
              colors.primaryFg,
              colors.primaryBorderAll ? `focus:${colors.primaryBorderAll.replace('border-', 'ring-')}` : 'focus:ring-indigo-500'
            )}
          />
        )}
        {isFavorite && (
          <span className={`px-2 py-0.5 text-xs font-medium bg-yellow-500 ${colors.primaryText} rounded`}>
            In Favorites
          </span>
        )}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
          <div className="flex-1 min-w-0">
            <div className={cn('text-sm font-medium truncate flex items-center gap-1', colors.text)}>
              {item.title}
              {isFavorite && <StarIcon className="w-3.5 h-3.5 text-yellow-500" filled />}
            </div>
            <div className={cn('text-xs truncate', colors.textSecondary)}>
              {item.fullPath || item.to}
            </div>
          </div>
        </div>
      </div>
      <button
        onClick={() => {
          if (isFavorite) {
            onRemoveFavorite(item.to);
          } else {
            onAddFavorite(item);
          }
        }}
        className={cn(
          'p-1.5 rounded-lg transition-colors ml-2',
          isFavorite
            ? 'text-yellow-500 hover:bg-yellow-100 dark:hover:bg-yellow-900/20'
            : `${colors.textMuted} hover:text-yellow-500 ${colors.interactiveHover}`
        )}
        title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
      >
        <StarIcon className="w-4 h-4" filled={isFavorite} />
      </button>
    </div>
  );
});

const FavoritesManager: React.FC<FavoritesManagerProps> = ({ open, onClose }) => {
  const { colors } = useTheme();
  const { showSuccess, showError, showInfo } = useNotification();
  const { user, can, permissionsLoaded } = useAuth();
  const canView = React.useMemo(
    () => (permissionsLoaded || user?.role === 'ADMIN' ? (moduleKey: string) => can(moduleKey, 'view') : undefined),
    [can, permissionsLoaded, user?.role]
  );
  const canEdit = React.useMemo(
    () => (permissionsLoaded || user?.role === 'ADMIN' ? (moduleKey: string) => can(moduleKey, 'edit') : undefined),
    [can, permissionsLoaded, user?.role]
  );
  const [favorites, setFavorites] = useState<FavoriteItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [availableItems, setAvailableItems] = useState<Array<{ title: string; to: string; icon?: React.ReactNode; fullPath: string }>>([]);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [editingRoute, setEditingRoute] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState<string>('');
  const editingTitleRef = useRef(editingTitle);
  editingTitleRef.current = editingTitle;

  useEffect(() => {
    if (open) {
      loadFavorites();
      loadAvailableItems();
      setSelectedItems(new Set());
    }
  }, [open]);


  const loadFavorites = async () => {
    try {
      setLoading(true);

      // Determine which routes are currently permitted for this user
      let allowedRoutes: Set<string> | undefined;
      try {
        const menuItems = getSidebarItems(user?.role, canView, canEdit);
        const allItems = getAllMenuItems(menuItems);
        allowedRoutes = new Set(allItems.map((item) => item.to));
      } catch (innerError) {
        console.warn('Failed to compute permitted routes for favorites, falling back to all favorites:', innerError);
      }

      const favs = await getFavorites();

      // If we successfully computed permitted routes, filter favorites to only permitted modules/forms
      const filteredFavs =
        allowedRoutes != null ? favs.filter((fav) => allowedRoutes!.has(fav.to)) : favs;

      setFavorites(filteredFavs);
    } catch (error: any) {
      console.error('Error loading favorites:', error);
      setFavorites([]);
      if (!error.message || (!error.message.includes('expired') && !error.message.includes('Unauthorized'))) {
        showError('Failed to load favorites');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableItems = () => {
    const menuItems = getSidebarItems(user?.role, canView, canEdit);
    const allItems = getAllMenuItems(menuItems);
    setAvailableItems(allItems);
  };

  const handleAddFavorite = useCallback(
    async (item: { title: string; to: string; fullPath?: string }) => {
      try {
        const result = await addToFavorites(item);
        if (result) {
          showSuccess('Added to favorites');
          await loadFavorites();
          loadAvailableItems();
        } else {
          showInfo('Already in favorites');
        }
      } catch (error: unknown) {
        console.error('Error adding favorite:', error);
        showError('Failed to add favorite: ' + (error instanceof Error ? error.message : 'Unknown error'));
      }
    },
    [showSuccess, showInfo, showError]
  );

  const handleRemoveFavorite = useCallback(
    async (route: string) => {
      try {
        const result = await removeFromFavorites(route);
        if (result) {
          showSuccess('Removed from favorites');
          await loadFavorites();
        }
      } catch (error: unknown) {
        console.error('Error removing favorite:', error);
        showError('Failed to remove favorite');
      }
    },
    [showSuccess, showError]
  );

  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    setTimeout(() => {
      (e.target as HTMLElement).style.opacity = '0.5';
    }, 0);
  }, []);

  const handleDragEnd = useCallback((e: React.DragEvent) => {
    (e.target as HTMLElement).style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex((prev) => (prev !== index ? index : prev));
  }, []);

  const handleDragLeave = useCallback(() => setDragOverIndex(null), []);

  const favoritesRef = useRef(favorites);
  favoritesRef.current = favorites;
  const handleDrop = useCallback(
    async (e: React.DragEvent, dropIndex: number) => {
      e.preventDefault();
      const di = draggedIndex;
      if (di === null || di === dropIndex) {
        setDraggedIndex(null);
        setDragOverIndex(null);
        return;
      }
      const currentFavs = favoritesRef.current;
      const newFavorites = [...currentFavs];
      const [draggedItem] = newFavorites.splice(di, 1);
      newFavorites.splice(dropIndex, 0, draggedItem);
      setFavorites(newFavorites);
      setDraggedIndex(null);
      setDragOverIndex(null);
      try {
        const result = await reorderFavorites(newFavorites);
        if (result) {
          showSuccess('Favorites reordered');
          window.dispatchEvent(new Event('favoritesUpdated'));
        } else {
          await loadFavorites();
          showError('Failed to save new order');
        }
      } catch (error) {
        console.error('Error reordering favorites:', error);
        await loadFavorites();
        showError('Failed to reorder favorites');
      }
    },
    [draggedIndex, showSuccess, showError]
  );

  const handleStartEdit = useCallback((fav: FavoriteItem) => {
    setEditingRoute(fav.to);
    setEditingTitle(fav.title);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingRoute(null);
    setEditingTitle('');
  }, []);

  const handleSaveEdit = useCallback(
    async (route: string) => {
      const newTitle = editingTitleRef.current.trim();
      if (!newTitle) {
        showInfo('Name cannot be empty');
        return;
      }
      const updatedFavorites = favorites.map((fav) =>
        fav.to === route ? { ...fav, title: newTitle } : fav
      );
      setFavorites(updatedFavorites);
      setEditingRoute(null);
      setEditingTitle('');
      try {
        const result = await reorderFavorites(updatedFavorites);
        if (!result) {
          showError('Failed to save favorite name');
          await loadFavorites();
        } else {
          showSuccess('Favorite name updated');
        }
      } catch (error) {
        console.error('Error updating favorite name:', error);
        showError('Failed to update favorite name');
        await loadFavorites();
      }
    },
    [favorites, showInfo, showSuccess, showError]
  );

  const handleToggleSelect = useCallback((item: { to: string }) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(item.to)) next.delete(item.to);
      else next.add(item.to);
      return next;
    });
  }, []);

  const handleSelectAll = () => {
    const favoriteRoutes = favorites.map(f => f.to);
    const notFavoriteItems = filteredAvailableItems.filter(item => !favoriteRoutes.includes(item.to));
    
    if (notFavoriteItems.length === 0) {
      setSelectedItems(new Set());
      return;
    }

    const allSelected = notFavoriteItems.every(item => selectedItems.has(item.to));
    if (allSelected) {
      // Deselect all
      const newSelected = new Set(selectedItems);
      notFavoriteItems.forEach(item => newSelected.delete(item.to));
      setSelectedItems(newSelected);
    } else {
      // Select all
      const newSelected = new Set(selectedItems);
      notFavoriteItems.forEach(item => newSelected.add(item.to));
      setSelectedItems(newSelected);
    }
  };

  const handleAddSelectedFavorites = async () => {
    if (selectedItems.size === 0) {
      showInfo('Please select at least one item');
      return;
    }

    try {
      setLoading(true);
      const favoriteRoutes = favorites.map(f => f.to);
      
      // Filter from all availableItems, not just filteredAvailableItems
      // This ensures we can add items even if they're not currently visible due to search
      const itemsToAdd = availableItems.filter(
        item => selectedItems.has(item.to) && !favoriteRoutes.includes(item.to)
      );


      if (itemsToAdd.length === 0) {
        showInfo('Selected items are already in favorites');
        setSelectedItems(new Set());
        setLoading(false);
        return;
      }

      // Map to the format expected by addMultipleToFavorites
      const itemsToAddFormatted = itemsToAdd.map(item => ({
        title: item.title,
        to: item.to,
        fullPath: item.fullPath,
      }));


      const result = await addMultipleToFavorites(itemsToAddFormatted);
      
      
      if (result.success) {
        showSuccess(result.message);
        await loadFavorites();
        loadAvailableItems(); // Refresh available items to update the UI
        setSelectedItems(new Set());
        // Trigger favorites update event for sidebar
        window.dispatchEvent(new Event('favoritesUpdated'));
      } else {
        showInfo(result.message || 'Failed to add selected items');
        await loadFavorites();
        loadAvailableItems(); // Refresh available items to update the UI
        setSelectedItems(new Set());
      }
    } catch (error: any) {
      console.error('Error adding selected favorites:', error);
      showError('Failed to add selected items: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const filteredAvailableItems = useMemo(() => {
    const searchLower = searchTerm.toLowerCase().trim();
    if (!searchLower) return availableItems;
    return availableItems.filter(
      (item) =>
        item.title.toLowerCase().includes(searchLower) ||
        item.fullPath?.toLowerCase().includes(searchLower) ||
        item.to?.toLowerCase().includes(searchLower)
    );
  }, [availableItems, searchTerm]);

  const groupedAvailableItems = useMemo(() => {
    const groups: Record<string, typeof availableItems> = {};
    filteredAvailableItems.forEach((item) => {
      const groupLabel = item.fullPath?.split(' > ')[0] || 'Other';
      if (!groups[groupLabel]) {
        groups[groupLabel] = [];
      }
      groups[groupLabel].push(item);
    });
    return groups;
  }, [filteredAvailableItems]);

  const favoriteRoutes = favorites.map(f => f.to);
  
  const isItemFavorite = (item: { to: string }) => favoriteRoutes.includes(item.to);

  return (
    <Modal isOpen={open} onClose={onClose} size="lg">
      <ModalHeader>
        <div className="flex items-center gap-2">
          <StarIcon className="w-5 h-5 text-yellow-500" filled />
          <ModalTitle>Manage Favorites</ModalTitle>
        </div>
        <button
          onClick={onClose}
          className={cn('p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors', colors.textSecondary)}
        >
          <XIcon className="w-5 h-5" />
        </button>
      </ModalHeader>
      <ModalContent>
        <div className="space-y-6">
          {/* Your Favorites Section */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className={cn('text-sm font-medium', colors.text)}>
                Your Favorites ({favorites.length})
              </h3>
              {favorites.length > 1 && (
                <span className={cn('text-xs', colors.textMuted)}>
                  Drag to reorder
                </span>
              )}
            </div>
            {favorites.length > 0 ? (
              <div className={cn('border rounded-lg divide-y', colors.border)}>
                {favorites.map((fav, index) => (
                  <FavoriteRow
                    key={fav.to}
                    fav={fav}
                    index={index}
                    isEditing={editingRoute === fav.to}
                    editingTitle={editingTitle}
                    onClose={onClose}
                    onEditingTitleChange={setEditingTitle}
                    onStartEdit={handleStartEdit}
                    onSaveEdit={handleSaveEdit}
                    onCancelEdit={handleCancelEdit}
                    onRemove={handleRemoveFavorite}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    draggedIndex={draggedIndex}
                    dragOverIndex={dragOverIndex}
                    colors={colors}
                  />
                ))}
              </div>
            ) : (
              <div className={cn('p-6 text-center border border-dashed rounded-lg', colors.border, colors.surface)}>
                <p className={cn('text-sm', colors.textSecondary)}>
                  No favorites yet. Add some below!
                </p>
              </div>
            )}
          </div>

          {/* Available Items Section */}
          <div>
            <div className="relative mb-3">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SearchIcon className={cn('w-4 h-4', colors.textMuted)} />
              </div>
              <TextInput
                type="text"
                placeholder="Search for forms to add..."
                value={searchTerm}
                onChange={(e: { target: { value: string } }) => setSearchTerm(e.target.value)}
                className="pl-10 w-full"
              />
            </div>

            <div className="flex items-center justify-between mb-2">
              <h3 className={cn('text-sm font-medium', colors.text)}>
                Available Forms ({filteredAvailableItems.length})
              </h3>
              <div className="flex items-center gap-2">
                {selectedItems.size > 0 && (
                  <span className={cn('text-xs px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300', colors.textSecondary)}>
                    {selectedItems.size} selected
                  </span>
                )}
                <Button variant="ghost" size="sm" onClick={handleSelectAll}>
                  {(() => {
                    const favoriteRoutes = favorites.map(f => f.to);
                    const notFavoriteItems = filteredAvailableItems.filter(item => !favoriteRoutes.includes(item.to));
                    const allSelected = notFavoriteItems.length > 0 && notFavoriteItems.every(item => selectedItems.has(item.to));
                    return allSelected ? 'Deselect All' : 'Select All';
                  })()}
                </Button>
                {selectedItems.size > 0 && (
                  <Button 
                    variant="default" 
                    size="sm" 
                    onClick={handleAddSelectedFavorites}
                    disabled={loading}
                    loading={loading}
                  >
                    Add Selected ({selectedItems.size})
                  </Button>
                )}
              </div>
            </div>

            <div className={cn('border rounded-lg', colors.border)}>
              {filteredAvailableItems.length > 0 ? (
                <div className="divide-y">
                  {Object.entries(groupedAvailableItems).map(([groupLabel, items]) => (
                    <div key={groupLabel}>
                      <div className={cn('px-3 py-2 text-xs font-semibold uppercase tracking-wide', colors.surface, colors.textSecondary)}>
                        {groupLabel}
                      </div>
                      <div className="divide-y">
                        {items.map((item) => (
                          <AvailableItemRow
                            key={item.to}
                            item={item}
                            isFavorite={favoriteRoutes.includes(item.to)}
                            isSelected={selectedItems.has(item.to)}
                            onToggleSelect={handleToggleSelect}
                            onAddFavorite={handleAddFavorite}
                            onRemoveFavorite={handleRemoveFavorite}
                            colors={colors}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={cn('p-6 text-center', colors.textSecondary)}>
                  <p className="text-sm">
                    {searchTerm ? 'No results found' : 'No items available'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </ModalContent>
      <ModalFooter>
        <Button onClick={onClose} variant="default">
          Done
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default FavoritesManager;

